import React, { useEffect, useState } from "react";
import axios from "axios";
import { Box, Typography, Divider, Button, Chip, Paper, Skeleton } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FullCardSkeleton } from "./_PortalSkeletons";
import { useActAsClient } from "./ActAsClientContext";

// Main app auth (staff) + portal auth (client)
import { auth } from "../../firebase-config";
import { authPortal } from "../../firebase-portal";

const GET_ASSIGNED_FORMS_URL =
  process.env.REACT_APP_GET_ASSIGNED_FORMS_URL ||
  "https://getclientforms-774073159618.us-central1.run.app";

export default function ClientPortalFormsTodo() {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const actAs = useActAsClient();
  const [params] = useSearchParams();

  const tsToLocale = (ts) =>
    ts?.toDate
      ? ts.toDate().toLocaleString()
      : ts?._seconds
      ? new Date(ts._seconds * 1000).toLocaleString()
      : ts
      ? new Date(ts).toLocaleString()
      : "Unknown";

  useEffect(() => {
    const isExpectingActAs = params.has("actAsClient") || params.has("as");
    if (isExpectingActAs && !actAs.isActingAs) return;

    const run = async () => {
      setLoading(true);
      try {
        const isActingAs = !!actAs?.isActingAs;

        const currentUser = isActingAs ? auth.currentUser : authPortal.currentUser;
        if (!currentUser) {
          throw new Error(isActingAs ? "No staff user" : "No portal user");
        }

        const idToken = await currentUser.getIdToken(true);

        const actAsHeaders = isActingAs ? actAs.getActAsHeaders() : {};
        const headers = { Authorization: `Bearer ${idToken}`, ...actAsHeaders };

        const { data } = await axios.post(GET_ASSIGNED_FORMS_URL, {}, { headers });
        setForms(Array.isArray(data?.forms) ? data.forms : []);
      } catch (e) {
        console.error("Load forms todo failed:", e);
        setForms([]);
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [actAs, params]);

  return (
    <Box>
      {actAs?.isActingAs && (
        <Box
          sx={{
            mb: 2,
            p: 1.25,
            borderRadius: 1,
            bgcolor: "#FFFBEB",
            border: "1px solid #FCD34D",
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, color: "#92400E" }}>
            Staff preview: acting as{" "}
            <span style={{ textDecoration: "underline" }}>
              {actAs.actAsName || actAs.clientId}
            </span>
            . All actions are recorded.
          </Typography>
        </Box>
      )}

      <div className="flex items-center gap-2">
        <Typography variant="h5" sx={{ mb: 2 }}>
          Forms To Do
        </Typography>
        {loading ? (
          <Skeleton variant="rounded" width={42} height={24} />
        ) : (
          <Chip size="small" label={`${forms?.length || 0}`} color="primary" variant="outlined" />
        )}
      </div>

      <Divider sx={{ mb: 2 }} />

      {loading && (
        <Box>
          {Array.from({ length: 6 }).map((_, i) => (
            <FullCardSkeleton key={`formtodo_skel_${i}`} rows={4} />
          ))}
        </Box>
      )}

      {!loading && forms.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No forms assigned.
        </Typography>
      )}

      {!loading && forms.length > 0 && (
        <Box>
          {forms.map((form, idx) => {
            const assignmentKey =
              form.assignmentId ||
              form.assignment_id ||
              form.assignmentID ||
              form.assignmentRef ||
              form.assignmentDocId ||
              form._id ||
              `${form.id || form.formId || "form"}-${
                form.createdAt?._seconds || form.createdAt || idx
              }`;

            const rawDue = form?.dueDate;
            const dueMs =
              rawDue && typeof rawDue === "object" && "_seconds" in rawDue
                ? rawDue._seconds * 1000
                : rawDue
                ? new Date(rawDue).getTime()
                : null;

            const hasValidDue = Number.isFinite(dueMs);
            const overdue = hasValidDue ? dueMs < Date.now() : false;

            const chipStyles = overdue
              ? { backgroundColor: "#FEE2E2", color: "#991B1B", border: "1px solid #FCA5A5" }
              : { backgroundColor: "#FEF3C7", color: "#92400E", border: "1px solid #FCD34D" };

            return (
              <Paper
                key={assignmentKey}
                variant="outlined"
                sx={{
                  p: 2,
                  mb: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div className="flex-1">
                  <div className="font-semibold">
                    {form?.title || form?.name || "Untitled Form"}
                  </div>
                  <div className="text-xs text-gray-500">
                    Last updated: {tsToLocale(form?.updatedAt)}
                  </div>
                  {hasValidDue && (
                    <span
                      title={new Date(dueMs).toLocaleString()}
                      style={{
                        display: "inline-block",
                        marginTop: 6,
                        borderRadius: 12,
                        padding: "2px 8px",
                        fontSize: 12,
                        fontWeight: 600,
                        ...chipStyles,
                      }}
                    >
                      {overdue ? "Overdue" : "Due"} {new Date(dueMs).toLocaleDateString()}
                    </span>
                  )}
                </div>

                <Button
                  size="small"
                  variant="contained"
                  onClick={() =>
                    navigate(`/portal/forms/fill/${form.id}`, {
                      state: {
                        clientMode: true,
                        assignmentId:
                          form.assignmentId ||
                          form.assignment_id ||
                          form.assignmentID ||
                          form.assignmentRef ||
                          form.assignmentDocId ||
                          form._id ||
                          null,
                      },
                    })
                  }
                >
                  Fill
                </Button>
              </Paper>
            );
          })}
        </Box>
      )}
    </Box>
  );
}